import { useState } from 'react';

import {
    PercentageCompleteIcon,
    PercentageIncompleteIcon,
    ProgressChevronIcon,
} from '@selina-finance/ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import {
    getCurrentAppData,
    useApplicationStore,
} from '@App/stores/applicationStore';
import { Help } from '@src/ui/components/Help/Help';

const classes = {
    base: 'hidden lg:block w-full',
    wrapper: 'bg-white p-6 rounded-lg w-full',
    progressWrapper: 'mb-4',
    progressText: 'text-xs font-bold text-periwinkle-900 whitespace-nowrap',
    progressTrack: 'h-2 bg-almond-500 mt-2 rounded-full',
    progressBar: 'h-full bg-periwinkle-700 rounded-full',
    list: 'mt-6',
    desktopNote: 'mt-4 text-sm text-center text-monochrome-600',
    item: 'flex justify-between items-center space-x-2 mb-1 rounded p-2 cursor-pointer',
    itemHover: 'hover:bg-neutral-40',
    itemActive: 'bg-almond-200',
    itemDisabled: 'cursor-not-allowed bg-transparent hover:bg-transparent',
    itemText: 'flex-1 ml-2',
    itemTitle: 'text-body font-sora-bold',
    itemTitleActive: 'text-midnight-600',
    itemTitleInactive: 'text-monochrome-400',
    mobileWrapper: 'lg:hidden bg-white rounded-lg shadow-md cursor-pointer',
    mobileButton: 'w-full px-4 py-3 text-left',
    mobileHeader:
        'grid w-full grid-cols-[auto,minmax(120px,1fr),auto] items-center gap-3 xs:flex xs:items-center xs:justify-center',
    mobileProgressRow: 'min-w-0 xs:ml-2 xs:mr-5',
    mobileProgressBar:
        'h-2 bg-almond-500 rounded-full w-full min-w-[120px] xs:w-[220px] xs:min-w-0 xs:max-w-[220px]',
    mobilePanel: 'px-4 pb-4',
    mobileInlineNote: 'mt-2 text-center text-xs text-monochrome-600',
};

type ProgressStepId = 'bank-details' | 'verify' | 'docs' | 'submit';

type ProgressStep = {
    id: ProgressStepId;
    title: string;
    route?: string;
};

export interface ProgressMenuProps {
    className?: string;
}

const ProgressMenu = ({ className }: ProgressMenuProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const steps: ProgressStep[] = [
        {
            id: 'bank-details',
            title: t('progress.addDetails'),
            route: '/bank-details',
        },
        { id: 'verify', title: t('progress.verifyIdentity'), route: '/verify' },
        { id: 'docs', title: t('progress.reviewDocs'), route: '/docs' },
        { id: 'submit', title: t('progress.getFunded'), route: '/submit' },
    ];

    const bankDetailsComplete = useApplicationStore(
        (state) => getCurrentAppData(state).bankDetailsComplete,
    );
    const verifyComplete = useApplicationStore(
        (state) => getCurrentAppData(state).verifyComplete,
    );
    const docsComplete = useApplicationStore(
        (state) => getCurrentAppData(state).docsComplete,
    );

    const [isOpen, setIsOpen] = useState(false);
    const toggleMobile = () => setIsOpen((prev) => !prev);

    const progress = (() => {
        if (docsComplete) return 75;
        if (verifyComplete) return 50;
        if (bankDetailsComplete) return 25;
        return 0;
    })();

    const handleNavigate = (route: string | undefined, isDisabled: boolean) => {
        if (isDisabled || !route) return;
        navigate(route);
    };

    const getStepState = (step: ProgressStep) => {
        const isActive = Boolean(
            step.route &&
                matchPath({ path: step.route, end: false }, location.pathname),
        );

        const isDisabled = (() => {
            switch (step.id) {
                case 'verify':
                    return !bankDetailsComplete;
                case 'docs':
                    return !verifyComplete;
                case 'submit':
                    return !docsComplete;
                default:
                    return false;
            }
        })();

        const isCompleted = (() => {
            switch (step.id) {
                case 'bank-details':
                    return bankDetailsComplete;
                case 'verify':
                    return verifyComplete;
                case 'docs':
                    return docsComplete;
                default:
                    return false;
            }
        })();

        return { isActive, isDisabled, isCompleted };
    };

    const renderStep = (step: ProgressStep) => {
        const { isActive, isDisabled, isCompleted } = getStepState(step);

        return (
            <li
                key={step.id}
                className={clsx(
                    classes.item,
                    !isDisabled && classes.itemHover,
                    isActive && classes.itemActive,
                    isDisabled && classes.itemDisabled,
                )}
                onClick={() => handleNavigate(step.route, isDisabled)}
            >
                <div className={classes.itemText}>
                    <p
                        className={clsx(
                            classes.itemTitle,
                            isActive
                                ? classes.itemTitleActive
                                : classes.itemTitleInactive,
                        )}
                    >
                        {step.title}
                    </p>
                </div>
                {isCompleted ? (
                    <PercentageCompleteIcon />
                ) : (
                    <PercentageIncompleteIcon />
                )}
            </li>
        );
    };

    return (
        <>
            <div className={clsx(className, classes.mobileWrapper)}>
                <button
                    type='button'
                    aria-expanded={isOpen}
                    className={classes.mobileButton}
                    onClick={toggleMobile}
                >
                    <div className={classes.mobileHeader}>
                        <p className={clsx(classes.progressText, 'shrink-0')}>
                            {t('progress.complete', { progress })}
                        </p>
                        <div className={classes.mobileProgressRow}>
                            <div className={classes.mobileProgressBar}>
                                <div
                                    className={classes.progressBar}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                        <div
                            className={clsx(
                                'h-5 w-5 shrink-0 text-steel-80 transition-transform',
                                isOpen && 'rotate-180',
                            )}
                        >
                            <ProgressChevronIcon />
                        </div>
                    </div>
                    <p className={classes.mobileInlineNote}>
                        {t('progress.restartWarning')}
                    </p>
                </button>

                {isOpen && (
                    <div className={classes.mobilePanel} onClick={toggleMobile}>
                        <ul>{steps.map(renderStep)}</ul>
                    </div>
                )}
            </div>
            <div className={clsx(className, classes.base)}>
                <div className={classes.wrapper}>
                    <div className={classes.progressWrapper}>
                        <p className={classes.progressText}>
                            {t('progress.complete', { progress })}
                        </p>
                        <div className={classes.progressTrack}>
                            <div
                                className={classes.progressBar}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    <ul className={classes.list}>{steps.map(renderStep)}</ul>
                    <p className={classes.desktopNote}>
                        {t('progress.restartWarning')}
                    </p>
                </div>
                <Help className='mt-2' />
            </div>
        </>
    );
};

export default ProgressMenu;
